import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Button,
  Typography,
} from '@material-ui/core';
import { 
  MoneyOff as MoneyOffIcon,
  Receipt as ReceiptIcon,
  ArrowBackIos as ArrowBackIosIcon
 } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { useParams, useHistory } from 'react-router-dom';
import makeBreadcrumbs from 'src/utils/breadcrumbs';
import PageWithBreadCrumbs from 'src/components/PageWithBreadCrumbs';
import PaymentInformationService from 'src/services/paymentInformationService';
import CircularLoader from 'src/components/CircularLoader';
import { createAxiosInstance } from 'src/utils/axios';
import NoResults from 'src/components/NoResults';
import { getRetailPaymentStatusFriendlyName, getRetailPaymentStatusByType } from 'src/utils/retailPaymentStatusTypes';
import { useSnackbar } from 'notistack';
import OriginalPaymenteInfo from './cards/OriginalPaymentInfo';
import { FeatureProvider } from 'src/context/FeatureContext';
import RefundInfo from './cards/RefundInfo';
import DisputeInfo from './cards/DisputeInfo';
import PaymentReceiptDialog from './modals/PaymentReceiptDialog';
import CancelPaymentDialog from './modals/CancelPaymentDialog';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    padding: theme.spacing(3),
  },
  noResultsContainer: {
    marginTop: '20%',
  },
  loadingContainer: {
    marginTop: '30%',
  },
  purchaseOrderSubHeaderValue: {
    color: theme.palette.secondary.main,
    paddingLeft: '5px',
    paddingRight: '10px',
  },
  backButton: {
    padding: '6px 15px',
    position: 'relative',
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    top: '-50px',
  },
  subHeaderButton: {
    padding: '5px',
  },
  cancelButton : {
    marginRight: '15px',
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  sendRecieptButton: {
    padding: '6px 15px',
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
  },
}));

const paymentInformationService = new PaymentInformationService(
  createAxiosInstance(process.env.REACT_APP_CASH_QORE_API)
);

function PurchaseOrderRetailPaymentDetail({refundFeatureIdentifier, cancelPaymentFeatureIdentifier, sendReceiptFeatureIdentifier}) {
  const classes = useStyles();
  const history = useHistory();
  const { cashQoreTransactionId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [retailPaymentDetails, setRetailPaymentDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [retailPaymentRefunds, SetRetailPaymentRefunds] = useState(null);
  const [showPaymentReceiptDialog, setShowPaymentReceiptDialog] = useState(false);
  const [showCancelPaymentDialog, setShowCancelPaymentDialog] = useState(false);

  useEffect(() => {
    getPaymentDetail(cashQoreTransactionId);
  }, [cashQoreTransactionId]);

  async function getPaymentDetail(transactionId) {
    try {
      setIsLoading(true);
      setIsError(false);
      const res = await paymentInformationService.getRetailPaymentDetails(transactionId);
      setRetailPaymentDetails(res.data.data);
      if (res.data.data?.Refunds){
        SetRetailPaymentRefunds(res.data.data?.Refunds);
      }
    } catch (e) {
      console.log(e);
      enqueueSnackbar('Error fetching retail payment details', {
        variant: 'error',
      });
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }

  const isPaymentOnHold = getRetailPaymentStatusByType("OnHold")?.id === Number(retailPaymentDetails?.RetailPaymentStatusId); // 1 = OnHold

  if (isError) {
    return (
      <div
        className={classes.noResultsContainer}
        title='Error getting payment details'
      >
        <NoResults />
      </div>
    );
  }
  const viewTitle = `Retail Payment ${retailPaymentDetails?.RetailPaymentId} Details`;

  return (
  <main className={classes.root}>
      <PageWithBreadCrumbs
        title={""}
        breadcrumbs={makeBreadcrumbs(viewTitle)}
      >
      {isLoading ? (
        <div className={classes.loadingContainer}>
          <CircularLoader />
        </div>
      ) : (
      <>
        <div className={classes.purchaseOrderSubHeader}>
          <Grid container sx={{ position: 'relative' }}>
            <Grid item xs={4}>
              <Typography variant='h5'>
                Payment Status:
                <Typography variant="contained" className={classes.purchaseOrderSubHeaderValue}>
                  {getRetailPaymentStatusFriendlyName(retailPaymentDetails?.RetailPaymentStatus)}
                </Typography>
              </Typography>
            </Grid>
            <Grid item xs={12} align="right">
              <Button className={classes.backButton} startIcon={<ArrowBackIosIcon />} onClick={() => {history.goBack();}}>
                GO Back
              </Button>
            </Grid>
            <Grid item xs={12} align="right" style={{ margin: '15px 0px' }}>
              <FeatureProvider featureIdentifier={cancelPaymentFeatureIdentifier}>
                <Button className={classes.cancelButton} disabled={!isPaymentOnHold} onClick={() => {setShowCancelPaymentDialog(true);}} startIcon={<MoneyOffIcon />}>
                  CANCEL PAYMENT
                </Button>
              </FeatureProvider>
              <FeatureProvider featureIdentifier={sendReceiptFeatureIdentifier}>  
                <Button className={classes.sendRecieptButton} startIcon={<ReceiptIcon />} onClick={() => {setShowPaymentReceiptDialog(true);}}>
                  SEND RECEIPT
                </Button>
              </FeatureProvider>
            </Grid>

          </Grid>
        </div>
        <Grid container spacing={6}>
          <Grid item xs={12} md={6}>
            <OriginalPaymenteInfo
              subtotal={retailPaymentDetails?.PaymentDetail?.SubTotal}
              tax={retailPaymentDetails?.PaymentDetail?.Tax}
              amountTotal={retailPaymentDetails?.PaymentDetail?.Total}
              currencyType={retailPaymentDetails?.PaymentDetail?.Currency}
              firstName={retailPaymentDetails?.Caller?.FirstName}
              lastName={retailPaymentDetails?.Caller?.LastName}
              invoiceId={retailPaymentDetails?.StripeInvoiceId}
              dateCreated={retailPaymentDetails?.DateCreated}
              callerEmail={retailPaymentDetails?.Caller?.Email}
              poNumber={retailPaymentDetails?.PoNumber}
              dispatchId={retailPaymentDetails?.DispatchId}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            {refundFeatureIdentifier && (
              <RefundInfo
                retailPayment={retailPaymentDetails}
                refunds={retailPaymentRefunds}
                refundfeatureIdentifier={refundFeatureIdentifier}
                onRefundSuccess={() => getPaymentDetail(cashQoreTransactionId)}
            />)}
            <DisputeInfo
              disputeDate={retailPaymentDetails?.DisputeDate}
              stripeDisputeId={retailPaymentDetails?.StripeDisputeId}
            />
          </Grid>
        </Grid>
        <CancelPaymentDialog
            isDialogOpen={showCancelPaymentDialog} 
            setIsDialogOpen={setShowCancelPaymentDialog} 
            handleClose={() => setShowCancelPaymentDialog(false)} 
            transactionId={cashQoreTransactionId}
            retailPaymentId={retailPaymentDetails?.RetailPaymentId}
            onSuccess={() => getPaymentDetail(cashQoreTransactionId)}
        >
        </CancelPaymentDialog>
        {(retailPaymentDetails?.RetailPaymentId) && 
          <PaymentReceiptDialog 
            isDialogOpen={showPaymentReceiptDialog} 
            setIsDialogOpen={setShowPaymentReceiptDialog} 
            handleClose={() => setShowPaymentReceiptDialog(false)} 
            retailPayment={retailPaymentDetails} 
           /> 
          }
        </>
      )}
      </PageWithBreadCrumbs>
    </main>
  );
}

PurchaseOrderRetailPaymentDetail.propTypes = {
  refundFeatureIdentifier: PropTypes.string.isRequired,
  cancelPaymentFeatureIdentifier: PropTypes.string.isRequired,
  sendReceiptFeatureIdentifier: PropTypes.string.isRequired,
};

export default PurchaseOrderRetailPaymentDetail;
